import React, {Suspense, lazy, useContext} from 'react';
import {Redirect, Switch} from 'react-router-dom';
import {Helmet} from 'react-helmet';
import './App.css';
import LoopMetadata from 'components/forms/context/LoopMeta';
import 'components/forms/locale/validation';
import {
    AuthenticatedRoute,
    PublicRoute,
    RegisteredRoute,
    UnAuthenticatedRoute,
    OnboardedRoute,
    AccountSuspendedRoute,
    LogoutRoute,
} from 'components/Route';
import LoadingPage from 'components/LoadingPage';
import SignIn from 'components/auth/SignIn';
import ResponsiveNav from 'components/ResponsiveNav';
import {useFlags} from 'hooks';
import {AppCtx} from 'context/AppContext';
import {googleTagManagerHead, googleTagManagerEnd} from 'pages/public/analytics';

import ScrollToTop from 'components/ScrollToTop';
import ReferralModal from 'components/referral/Modal';
import {NoUserRoute} from 'components/Route';
const Logout = lazy(() => import('pages/public/Logout'));
const AddDocs = lazy(() => import('components/AddDocs'));
const AddSeaservice = lazy(() => import('components/AddSeaservice'));
const AddTraining = lazy(() => import('components/AddTraining'));
const AddOtherTraining = lazy(() => import('components/AddOtherTraining'));
const AddUniformedService = lazy(() => import('components/AddUniformedService'));
const AddTrackedSeaservice = lazy(() => import('components/AddTrackedSeaservice'));
const AddUSCGService = lazy(() => import('components/AddUSCGService'));
const DocumentDetail = lazy(() => import('components/documents/Detail'));
const DocumentsPage = lazy(() => import('pages/DocumentsPage'));
const GuidancePage = lazy(() => import('pages/GuidancePage'));
const OnboardPage = lazy(() => import('pages/Onboard'));
const VerifyEmail = lazy(() => import('components/auth/VerifyEmail'));
const SignaturePage = lazy(() => import('pages/SignaturePage'));

const IndexPageRenew = lazy(() => import('pages/public/IndexPageRenew'));
const LandingPageCG719 = lazy(() => import('pages/public/LandingPageCG719'));

const ProductsPage = lazy(() => import('pages/public/ProductsPage'));
const ResourcesPage = lazy(() => import('pages/public/ResourcesPage'));
const NewsroomPage = lazy(() => import('pages/public/NewsroomPage'));
const PerfectApplicationsPage = lazy(() =>
    import('pages/public/PerfectApplicationsPage')
);
const TeamPage = lazy(() => import('pages/public/TeamPage'));
const PricingPage = lazy(() => import('pages/public/PricingPage'));

const AdminPage = lazy(() => import('pages/AdminPage'));
const CreateApplication = lazy(() =>
    import('components/admin/applications/CreateApplication')
);
const AccountingPage = lazy(() => import('pages/AccountingPage'));
const EmployerPortalPage = lazy(() => import('pages/EmployerPortalPage'));
const AllUsersPortal = lazy(() =>
    import('pages/EmployerPortalPage').then(module => ({
        default: module.AllUsersPortal,
    }))
);
const CredentialPage = lazy(() => import('pages/CredentialPage'));
const SeaservicesPage = lazy(() => import('pages/SeaservicesPage'));
const SettingsPage = lazy(() => import('pages/SettingsPage'));
const ProfilePage = lazy(() => import('pages/settings/Profile'));
const TypographyPage = lazy(() => import('pages/TypographyPage'));

const ToS = lazy(() => import('pages/ToS'));
const Privacy = lazy(() => import('pages/Privacy'));
const Refunds = lazy(() => import('pages/Refunds'));
const ProgressPage = lazy(() => import('pages/ProgressPage'));
const TrainingPage = lazy(() => import('pages/TrainingPage'));

const SubmitApplicationPage = lazy(() => import('pages/SubmitApplicationPage'));
const PaymentFailed = lazy(() => import('pages/suspended/PaymentFailed'));
const Unsubscribed = lazy(() => import('pages/suspended/Unsubscribed'));
const ConfirmPayment = lazy(() => import('pages/suspended/ConfirmPayment'));
const SuspendedPage = lazy(() => import('pages/suspended/SuspendedPage'));
const ChooseSubscription = lazy(() => import('pages/suspended/ChooseSubscription'));
const NoUserPage = lazy(() => import('pages/NoUserPage'));

const showAnalytics = () => !window.SNAPSHOT && !window.Cypress;

const App = () => {
    const {my_progress_page_active} = useFlags();
    const {config} = useContext(AppCtx);
    return (
        <ResponsiveNav>
            <ScrollToTop />
            {window.sessionStorage.getItem('REFERRAL_CODE') ? (
                <ReferralModal />
            ) : null}
            <Helmet>
                <meta name='theme-color' content='#0B5D98' />
                <meta
                    name='description'
                    content='Obtaining, tracking and renewing U.S. Coast Guard (USCG) credentials is now easy with MM-SEAS™. Empowering maritime professionals so they can focus on their lives and not mundane paperwork.'
                />
                <meta
                    name='keywords'
                    content="Coast Guard License, USCG License, Captain's License, Mates License, OUPV, MMC, USCG, merchant mariner credential, USCG License, Paris Hilton"
                />
                <meta
                    name='MM-SEAS'
                    content='Merchant Mariner Secure Electronic Application Software'
                />

                <meta
                    property='og:title'
                    content='Merchant Mariner Secure Electronic Application Software'
                />
                <meta
                    property='og:description'
                    content='Obtaining, tracking and renewing U.S. Coast Guard (USCG) credentials is now easy with MM-SEAS™. Empowering maritime professionals so they can focus on their lives and not mundane paperwork.'
                />
                <meta
                    property='og:image'
                    content={`${process.env.PUBLIC_URL}/sharelogo.jpg`}
                />
                <meta name='twitter:card' content='summary_large_image' />

                <meta property='og:site_name' content='MM-SEAS' />

                {/* Facebook Meta Tags */}
                <meta property='og:type' content='website' />

                {/* Twitter Meta Tags */}
                <meta
                    property='twitter:domain'
                    content={`${process.env.PUBLIC_URL}`}
                />
                <meta
                    name='twitter:title'
                    content='Merchant Mariner Secure Electronic Application Software'
                />
                <meta
                    name='twitter:description'
                    content='Obtaining, tracking and renewing U.S. Coast Guard (USCG) credentials is now easy with MM-SEAS™. Empowering maritime professionals so they can focus on their lives and not mundane paperwork.'
                />
                <meta
                    name='twitter:image'
                    content={`${process.env.PUBLIC_URL}/sharelogo.jpg`}
                />
                {showAnalytics() && config.googleTagManager ? (
                    <script>{googleTagManagerHead(config.googleTagManager)}</script>
                ) : null}
            </Helmet>
            <Suspense fallback={<LoadingPage />}>
                <Switch>
                    <UnAuthenticatedRoute
                        path='/'
                        exact
                        render={() => <Redirect to='/signin' />}
                    />
                    <UnAuthenticatedRoute
                        path='/renew'
                        exact
                        component={IndexPageRenew}
                    />
                    <UnAuthenticatedRoute
                        path='/cg719'
                        exact
                        component={LandingPageCG719}
                    />
                    <PublicRoute path='/product' exact component={ProductsPage} />
                    <PublicRoute path='/resources' exact component={ResourcesPage} />
                    <PublicRoute path='/newsroom' exact component={NewsroomPage} />
                    <PublicRoute
                        path='/perfect-applications'
                        exact
                        component={PerfectApplicationsPage}
                    />
                    <PublicRoute path='/team' exact component={TeamPage} />
                    <PublicRoute path='/pricing' exact component={PricingPage} />
                    <PublicRoute path='/terms-of-use' exact component={ToS} />
                    <PublicRoute path='/privacy' exact component={Privacy} />
                    <PublicRoute path='/refunds' exact component={Refunds} />
                    <PublicRoute path='/training' exact component={TrainingPage} />
                    <LogoutRoute path='/logout' exact component={Logout} />
                    <LogoutRoute path='/logout' exact component={Logout} />
                    <UnAuthenticatedRoute path='/signin' exact component={SignIn} />
                    <PublicRoute
                        path='/referral'
                        exact
                        render={() => <SignIn referral />}
                    />

                    <AuthenticatedRoute
                        path='/verify-email'
                        component={VerifyEmail}
                    />
                    <RegisteredRoute path='/onboard' component={OnboardPage} />
                    <OnboardedRoute path='/profile' exact component={ProfilePage} />
                    <OnboardedRoute
                        path='/settings'
                        exact
                        component={SettingsPage}
                    />
                    <OnboardedRoute path='/settings/:tab' component={SettingsPage} />
                    <OnboardedRoute
                        path='/seaservice'
                        exact
                        component={SeaservicesPage}
                    />
                    <OnboardedRoute
                        path='/seaservice/:tab'
                        component={SeaservicesPage}
                    />
                    <OnboardedRoute
                        path='/admin'
                        exact
                        component={AdminPage}
                        permission='canViewUsers'
                    />
                    <OnboardedRoute
                        path='/admin/typography'
                        component={TypographyPage}
                        permission='canViewUsers'
                    />
                    <OnboardedRoute
                        path='/admin/applications/create/:step?'
                        component={CreateApplication}
                        permission='canViewUsers'
                    />
                    <OnboardedRoute
                        path='/admin/:tab/'
                        component={AdminPage}
                        permission='canViewUsers'
                    />
                    <OnboardedRoute
                        path='/accounting'
                        exact
                        component={AccountingPage}
                        permission='canViewPayment'
                    />
                    <OnboardedRoute
                        path='/accounting/:tab'
                        component={AccountingPage}
                        permission='canViewPayment'
                    />
                    <OnboardedRoute
                        path='/portal'
                        exact
                        component={EmployerPortalPage}
                        permission='canViewEmployerPortal'
                    />
                    <OnboardedRoute
                        path='/portal/all'
                        exact
                        component={AllUsersPortal}
                        permission='canViewOrgs'
                    />
                    <OnboardedRoute
                        path='/portal/:orgId'
                        exact
                        component={EmployerPortalPage}
                        permission='canViewEmployerPortal'
                    />
                    <OnboardedRoute
                        path='/portal/:orgId/:tab'
                        component={EmployerPortalPage}
                        permission='canViewEmployerPortal'
                    />
                    <OnboardedRoute
                        path='/documents'
                        exact
                        component={DocumentsPage}
                    />
                    <OnboardedRoute
                        path='/documents/:tab'
                        component={DocumentsPage}
                    />
                    <OnboardedRoute
                        path='/guidance'
                        exact
                        component={GuidancePage}
                    />
                    <OnboardedRoute
                        path='/guidance/:tab'
                        exact
                        component={GuidancePage}
                    />
                    <OnboardedRoute
                        path='/guidance/:tab/:subtab'
                        component={GuidancePage}
                    />
                    <OnboardedRoute
                        path='/applications/submit'
                        exact
                        component={SubmitApplicationPage}
                    />

                    <OnboardedRoute
                        path='/document/:docType/:docId'
                        component={DocumentDetail}
                    />
                    <OnboardedRoute path='/adddocs' component={AddDocs} />
                    <OnboardedRoute
                        path='/addseaservice'
                        component={AddSeaservice}
                    />
                    <OnboardedRoute path='/addtraining' component={AddTraining} />
                    <OnboardedRoute
                        path='/addothertraining'
                        component={AddOtherTraining}
                    />
                    <OnboardedRoute
                        path='/addtrackedss'
                        component={AddTrackedSeaservice}
                    />
                    <OnboardedRoute
                        path='/uniformedservice/uscg'
                        component={AddUSCGService}
                    />
                    <OnboardedRoute
                        path='/uniformedservice'
                        component={AddUniformedService}
                    />
                    <OnboardedRoute
                        path='/application/sign'
                        component={SignaturePage}
                    />
                    <OnboardedRoute
                        path='/forms/:form/vessel/:vesselStep'
                        render={({match}) => {
                            const formName = match.params.form;
                            const Form =
                                require(`components/forms/${formName}/index.js`).default;
                            return (
                                <LoopMetadata>
                                    <Form />
                                </LoopMetadata>
                            );
                        }}
                    />
                    <OnboardedRoute
                        path='/forms/:form/:formStep'
                        render={({match}) => {
                            const formName = match.params.form;
                            const Form =
                                require(`components/forms/${formName}/index.js`).default;
                            return (
                                <LoopMetadata>
                                    <Form />
                                </LoopMetadata>
                            );
                        }}
                    />

                    <OnboardedRoute
                        path='/forms/:form'
                        render={({match}) => {
                            return <Redirect to={`${match.url}/start`} />;
                        }}
                    />
                    {my_progress_page_active && (
                        <OnboardedRoute path='/progress' component={ProgressPage} />
                    )}
                    <OnboardedRoute path='/home' exact component={CredentialPage} />
                    <OnboardedRoute path='/home/:tab' component={CredentialPage} />
                    <AccountSuspendedRoute
                        path='/payment-failed'
                        component={PaymentFailed}
                    />
                    <AccountSuspendedRoute
                        path='/unsubscribed'
                        component={Unsubscribed}
                    />
                    <AccountSuspendedRoute
                        path='/confirm-payment'
                        component={ConfirmPayment}
                    />
                    <AccountSuspendedRoute
                        path='/choose-subscription'
                        component={ChooseSubscription}
                    />
                    <AccountSuspendedRoute
                        path='/suspended'
                        component={SuspendedPage}
                    />
                    <NoUserRoute path='/user-404' component={NoUserPage} />
                    <OnboardedRoute
                        path='/'
                        render={() => {
                            return <Redirect to='/home' />;
                        }}
                    />
                    <PublicRoute
                        path='*'
                        render={() => {
                            return <Redirect to='/' />;
                        }}
                    />
                </Switch>
            </Suspense>
            {showAnalytics() && config.googleTagManager ? (
                <noscript>
                    {/* eslint-disable-next-line jsx-a11y/iframe-has-title*/}
                    <iframe
                        height='0'
                        width='0'
                        style={{
                            display: 'none',
                            visibility: 'hidden',
                        }}
                        src={googleTagManagerEnd(config.googleTagManager)}
                    ></iframe>
                </noscript>
            ) : null}
        </ResponsiveNav>
    );
};

export default App;
